import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import Chart from 'react-apexcharts';
import useElementWidth from '../../hooks/useElementWidth';

const EmployeeDashboardPayrollMonthlyChart = ({ monthlyPayments = [], isLoading }) => {
  const [chartWrapRef, chartWidth] = useElementWidth();
  const normalizedPayments = Array.isArray(monthlyPayments)
    ? monthlyPayments
        .map((m) => ({
          month: m?.month ?? m?.label ?? '',
          amount: Number(m?.amount),
        }))
        .filter((m) => m.month && Number.isFinite(m.amount))
    : [];

  const categories = normalizedPayments.map((m) => m.month);
  const amounts = normalizedPayments.map((m) => m.amount);
  const hasPayments = normalizedPayments.length > 0;

  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '55%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: categories.length ? categories : ['—'],
      labels: {
        rotate: -45,
        rotateAlways: categories.length > 6,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      title: { text: 'Amount' },
      labels: {
        formatter: (val) =>
          typeof val === 'number'
            ? val.toLocaleString(undefined, { maximumFractionDigits: 0 })
            : val,
      },
    },
    colors: ['#0A9EF2'],
    grid: { borderColor: '#f1f1f1' },
    tooltip: {
      y: {
        formatter: (val) =>
          typeof val === 'number'
            ? val.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
            : val,
      },
    },
  };

  const series = [
    {
      name: 'Payment',
      data: amounts.length ? amounts : [0],
    },
  ];

  const cardSx = { height: '100%', width: '100%', display: 'flex', flexDirection: 'column' };
  const contentSx = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '&:last-child': { pb: 2 },
  };

  if (isLoading) {
    return (
      <Card sx={cardSx}>
        <CardContent sx={contentSx}>
          <Typography variant="h6" gutterBottom>
            Payroll by month
          </Typography>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 320,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={cardSx}>
      <CardContent sx={contentSx}>
        <Typography variant="h6" gutterBottom>
          Payroll by month
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Payments recorded per month in the selected range.
        </Typography>
        {hasPayments !== true ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 320,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
            }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                No payroll data
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are no monthly payments in the selected range.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            ref={chartWrapRef}
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 320,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {chartWidth > 0 ? (
              <Chart options={options} series={series} type="bar" height={320} width={chartWidth} />
            ) : (
              <CircularProgress size={32} />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeDashboardPayrollMonthlyChart;
